import React, { useMemo } from 'react';
import {
  AE_CHART_AGGREGATE_COLOR,
  AE_CHART_BENCHMARK_COLOR,
  AE_CHART_FILL_OPACITY,
  AE_CHART_LABEL_CLASS,
  AE_CHART_SCORE_COLOR,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';

export interface GapChartSection {
  section_id: string;
  section_name: string;
  mean_score: number | null;
  gap?: number | null;
}

interface GapRadarChartProps {
  sections: GapChartSection[];
  scaleMax?: number;
  /** Peer benchmark (B1) means keyed by section_id */
  aggregateBySection?: Record<string, number | null>;
  /** Optional peer benchmark means keyed by section_id */
  benchmarkBySection?: Record<string, number | null>;
  yourScoreLabel?: string;
  aggregateLabel?: string;
  benchmarkLabel?: string;
  className?: string;
}

function polygonPoints(
  scored: GapChartSection[],
  valuesBySection: Record<string, number | null>,
  n: number,
  cx: number,
  cy: number,
  maxR: number,
  scaleMax: number
): string | null {
  const pts = scored.map((sec, i) => {
    const value = valuesBySection[sec.section_id];
    if (value == null) return null;
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const r = maxR * Math.min(1, Math.max(0, value / scaleMax));
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });
  if (pts.some((p) => p == null)) return null;
  return pts.map((p) => `${p!.x},${p!.y}`).join(' ');
}

/** Word-wrap axis labels — full text, no ellipsis truncation. */
function wrapAxisLabel(name: string, maxCharsPerLine = 16): string[] {
  const trimmed = name.trim();
  if (!trimmed) return [''];
  if (trimmed.length <= maxCharsPerLine) return [trimmed];

  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxCharsPerLine && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function axisLabelStyle(angle: number): {
  textAnchor: 'start' | 'middle' | 'end';
  dominantBaseline: 'middle' | 'hanging' | 'alphabetic';
} {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  let textAnchor: 'start' | 'middle' | 'end' = 'middle';
  if (cos > 0.2) textAnchor = 'start';
  else if (cos < -0.2) textAnchor = 'end';

  let dominantBaseline: 'middle' | 'hanging' | 'alphabetic' = 'middle';
  if (sin > 0.2) dominantBaseline = 'hanging';
  else if (sin < -0.2) dominantBaseline = 'alphabetic';

  return { textAnchor, dominantBaseline };
}

/**
 * Radar chart: respondent score vs optional peer benchmark (B1).
 * Pure SVG — no chart library dependency.
 */
export const GapRadarChart: React.FC<GapRadarChartProps> = ({
  sections,
  scaleMax = 5,
  aggregateBySection,
  benchmarkBySection,
  yourScoreLabel = 'Your score',
  aggregateLabel = 'Benchmark',
  benchmarkLabel = 'Peer benchmark',
  className = '',
}) => {
  const scored = sections.filter((s) => s.mean_score != null);
  const n = scored.length;
  const chartSize = 280;
  const labelPad = 88;
  const size = chartSize + labelPad * 2;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = chartSize * 0.36;
  const labelRadius = maxR + 30;

  const points = useMemo(() => {
    if (!n) return [];
    return scored.map((sec, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const ratio = (sec.mean_score ?? 0) / scaleMax;
      const r = maxR * Math.min(1, Math.max(0, ratio));
      const alignment = axisLabelStyle(angle);
      return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
        labelX: cx + labelRadius * Math.cos(angle),
        labelY: cy + labelRadius * Math.sin(angle),
        lines: wrapAxisLabel(sec.section_name),
        ...alignment,
      };
    });
  }, [scored, n, cx, cy, maxR, labelRadius, scaleMax]);

  const aggregatePolygon = useMemo(() => {
    if (!aggregateBySection || !n) return null;
    return polygonPoints(scored, aggregateBySection, n, cx, cy, maxR, scaleMax);
  }, [aggregateBySection, scored, n, cx, cy, maxR, scaleMax]);

  const benchmarkPolygon = useMemo(() => {
    if (!benchmarkBySection || !n) return null;
    return polygonPoints(scored, benchmarkBySection, n, cx, cy, maxR, scaleMax);
  }, [benchmarkBySection, scored, n, cx, cy, maxR, scaleMax]);

  if (!n) {
    return <p className={AE_MUTED}>No scored sections to chart.</p>;
  }

  const polygon = points.map((p) => `${p.x},${p.y}`).join(' ');

  const rings = [0.25, 0.5, 0.75, 1].map((f) => (
    <circle
      key={f}
      cx={cx}
      cy={cy}
      r={maxR * f}
      fill="none"
      stroke="var(--color-border)"
      strokeWidth={1}
    />
  ));

  return (
    <div className={`overflow-visible ${className}`.trim()}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="mx-auto w-full max-w-lg overflow-visible"
        role="img"
        overflow="visible"
      >
        <title>Section scores comparison</title>
        {rings}
        {benchmarkPolygon && (
          <polygon
            points={benchmarkPolygon}
            fill={AE_CHART_BENCHMARK_COLOR}
            fillOpacity={AE_CHART_FILL_OPACITY}
            stroke={AE_CHART_BENCHMARK_COLOR}
            strokeWidth={2}
            strokeDasharray="5 4"
          />
        )}
        {aggregatePolygon && (
          <polygon
            points={aggregatePolygon}
            fill={AE_CHART_AGGREGATE_COLOR}
            fillOpacity={AE_CHART_FILL_OPACITY}
            stroke={AE_CHART_AGGREGATE_COLOR}
            strokeWidth={2.5}
          />
        )}
        <polygon
          points={polygon}
          fill={AE_CHART_SCORE_COLOR}
          fillOpacity={AE_CHART_FILL_OPACITY + 0.06}
          stroke={AE_CHART_SCORE_COLOR}
          strokeWidth={2.5}
        />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill={AE_CHART_SCORE_COLOR} />
            <text
              x={p.labelX}
              y={p.labelY}
              textAnchor={p.textAnchor}
              dominantBaseline={p.dominantBaseline}
              fill="var(--color-surface-text)"
              stroke="var(--color-surface-bg)"
              strokeWidth={3}
              paintOrder="stroke"
              fontSize={10}
              fontWeight={500}
            >
              {p.lines.map((line, lineIdx) => (
                <tspan key={lineIdx} x={p.labelX} dy={lineIdx === 0 ? 0 : '1.15em'}>
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        ))}
      </svg>
      <div className={`${AE_CHART_LABEL_CLASS} mt-2 flex flex-wrap justify-center gap-4`}>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded-sm border-2"
            style={{
              backgroundColor: AE_CHART_SCORE_COLOR,
              borderColor: AE_CHART_SCORE_COLOR,
              opacity: 0.85,
            }}
          />
          {yourScoreLabel}
        </span>
        {aggregatePolygon && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-sm border-2"
              style={{
                backgroundColor: AE_CHART_AGGREGATE_COLOR,
                borderColor: AE_CHART_AGGREGATE_COLOR,
                opacity: 0.85,
              }}
            />
            {aggregateLabel}
          </span>
        )}
        {benchmarkPolygon && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-sm border-2 border-dashed bg-transparent"
              style={{ borderColor: AE_CHART_BENCHMARK_COLOR }}
            />
            {benchmarkLabel}
          </span>
        )}
      </div>
    </div>
  );
};
